import { useState } from 'react';
import Swal from 'sweetalert2';
import type { NocCriticalAlert } from '@/mocks/superAdminDashboard';

interface NocCriticalAlertsProps {
  alerts: NocCriticalAlert[];
}

const typeAccent: Record<string, { icon: string; bg: string; color: string }> = {
  service_down:     { icon: 'ri-shut-down-line', bg: 'bg-red-100', color: 'text-red-500' },
  backup_failed:    { icon: 'ri-file-copy-2-line', bg: 'bg-red-100', color: 'text-red-500' },
  license_expiring: { icon: 'ri-calendar-check-line', bg: 'bg-amber-100', color: 'text-amber-600' },
  storage_low:      { icon: 'ri-hard-drive-2-line', bg: 'bg-amber-100', color: 'text-amber-600' },
  api_disconnected: { icon: 'ri-plug-line', bg: 'bg-red-100', color: 'text-red-500' },
};

const severityBadge: Record<string, { label: string; bg: string; color: string }> = {
  critical: { label: 'Crítico', bg: 'bg-red-100', color: 'text-red-500' },
  warning:  { label: 'Alerta', bg: 'bg-amber-100', color: 'text-amber-600' },
};

export default function NocCriticalAlerts({ alerts }: NocCriticalAlertsProps) {
  const [dismissed, setDismissed] = useState<Set<number>>(new Set());
  const [expanded, setExpanded] = useState(false);

  const visible = alerts.filter((a) => !dismissed.has(a.id));
  const criticalCount = visible.filter((a) => a.severity === 'critical').length;
  const displayed = expanded ? visible : visible.slice(0, 4);
  const hasMore = visible.length > 4;

  const handleDismiss = async (alert: NocCriticalAlert) => {
    const result = await Swal.fire({
      title: 'Descartar alerta',
      text: `¿Ocultar "${alert.title}"?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Descartar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#d1d5db',
      customClass: {
        popup: 'rounded-2xl',
        title: 'font-bold text-sm',
        confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
        cancelButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
      },
      buttonsStyling: false,
    });
    if (result.isConfirmed) {
      setDismissed((prev) => new Set(prev).add(alert.id));
    }
  };

  if (visible.length === 0) {
    return (
      <div className="bg-background-50 border border-background-200 rounded-2xl p-5">
        <div className="flex flex-col items-center text-center gap-3 py-4">
          <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
            <i className="ri-check-double-line text-emerald-500 text-xl"></i>
          </div>
          <p className="text-foreground-800 text-sm font-semibold">Sin alertas críticas</p>
          <p className="text-foreground-400 text-[11px]">Todos los sistemas operan con normalidad.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-background-200">
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${criticalCount > 0 ? 'bg-red-100' : 'bg-amber-100'}`}>
            <i className={`${criticalCount > 0 ? 'ri-error-warning-fill text-red-500' : 'ri-alert-fill text-amber-500'} text-sm`}></i>
          </div>
          <div>
            <h2 className="text-foreground-950 font-semibold text-sm">Alertas Críticas — NOC</h2>
            <p className="text-foreground-400 text-[10px]">
              {visible.length} alerta{visible.length !== 1 ? 's' : ''} · {criticalCount} crítica{criticalCount !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
        {dismissed.size > 0 && (
          <button
            onClick={() => setDismissed(new Set())}
            className="text-foreground-400 text-[10px] font-medium hover:text-foreground-600 cursor-pointer transition-colors whitespace-nowrap"
          >
            Restaurar {dismissed.size}
          </button>
        )}
      </div>

      {/* Alert list */}
      <div className="divide-y divide-background-200">
        {displayed.map((alert, idx) => {
          const cfg = typeAccent[alert.type];
          const sev = severityBadge[alert.severity];
          return (
            <div
              key={alert.id}
              className="flex items-start gap-3 px-5 py-3 hover:bg-background-100 transition-colors group"
              style={{ animation: `fadeInUp 0.3s ease-out forwards`, animationDelay: `${idx * 50}ms`, opacity: 0 }}
            >
              {/* Icon */}
              <div className={`w-8 h-8 rounded-lg ${cfg.bg} flex items-center justify-center shrink-0`}>
                <i className={`${cfg.icon} ${cfg.color} text-xs`}></i>
              </div>

              {/* Content */}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap mb-0.5">
                  <span className="text-foreground-800 text-xs font-semibold">{alert.title}</span>
                  <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold ${sev.bg} ${sev.color}`}>
                    {sev.label}
                  </span>
                </div>
                <p className="text-foreground-500 text-[10px] leading-relaxed">{alert.description}</p>
                <span className="text-foreground-400 text-[9px] mt-1 inline-block">{alert.timeRelative}</span>
              </div>

              {/* Dismiss */}
              <button
                onClick={() => handleDismiss(alert)}
                className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center hover:bg-background-200 opacity-0 group-hover:opacity-100 transition-all cursor-pointer shrink-0"
                title="Descartar alerta"
              >
                <i className="ri-close-line text-foreground-400 text-xs"></i>
              </button>
            </div>
          );
        })}

        {hasMore && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="w-full py-2.5 text-foreground-400 text-[10px] font-medium hover:text-foreground-600 hover:bg-background-100 cursor-pointer transition-colors whitespace-nowrap"
          >
            {expanded ? 'Ver menos' : `Ver todas (${visible.length})`}
          </button>
        )}
      </div>
    </div>
  );
}